import {
    useEffect,
    useRef,
    useState,
    type ComponentProps,
    type JSXElementConstructor,
    type ReactNode,
} from 'react';
import { ProjectTile } from '@archipro-design/aria';
import type { LogImpressionProps } from '@archipro-website/tracker';
import { useLogImpression, useTracker } from '@archipro-website/tracker';
import { useInViewport } from 'ahooks';

/** Fluent/Aria slot/shorthand props widen `ProjectTile` enough that raw JSX hits TS2590. */
const RenderProjectTile = ProjectTile as JSXElementConstructor<
    ComponentProps<typeof ProjectTile>
>;

export type TileProps = ComponentProps<typeof ProjectTile> & {
    impression?: LogImpressionProps;
    sectionSource?: 'Directory' | 'Profile' | 'Search';
};

const ProjectImpressionTile = (props: TileProps) => {
    const { impression, sectionSource, children, ...rest } = props;
    const ref = useRef<HTMLDivElement>(null);

    useLogImpression({ data: impression, ref });
    const tracker = useTracker();

    const [inViewport] = useInViewport(ref, {
        threshold: 0,
    });

    const [beenInViewport, setBeenInViewport] = useState(inViewport);

    useEffect(() => {
        if (inViewport && !beenInViewport) {
            setBeenInViewport(true);
        }
    }, [inViewport, beenInViewport]);

    const carouselTracker = (direction: 'next' | 'prev') => {
        if (impression) {
            switch (direction) {
                case 'next':
                    tracker.log('CarouselNext', {
                        url: new URL(window.location.href),
                        targetTracker: ['archiproTracker', 'ga4Tracker'],
                        data: {
                            ExtraData: JSON.stringify({
                                source: sectionSource,
                                libraryItemId: impression.itemID,
                            }),
                        },
                        ga4Data: {
                            source: sectionSource,
                            libraryItemId: impression.itemID,
                        },
                    });
                    break;
                case 'prev':
                    tracker.log('CarouselPrev', {
                        url: new URL(window.location.href),
                        targetTracker: ['archiproTracker', 'ga4Tracker'],
                        data: {
                            ExtraData: JSON.stringify({
                                source: sectionSource,
                                libraryItemId: impression.itemID,
                            }),
                        },
                        ga4Data: {
                            source: sectionSource,
                            libraryItemId: impression.itemID,
                        },
                    });
                    break;
            }
        }
    };

    const projectTileProps: ComponentProps<typeof ProjectTile> = {
        ...rest,
        inViewport: beenInViewport,
        trackCarouselActivity: carouselTracker,
    };

    return (
        <RenderProjectTile ref={ref} {...projectTileProps}>
            {children as ReactNode}
        </RenderProjectTile>
    );
};

export default ProjectImpressionTile;
